import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    Legend
} from 'recharts';
import { TrendingUp, Package, DollarSign, BarChart3 } from 'lucide-react';

const COLORS = ['hsl(221, 83%, 53%)', 'hsl(160, 84%, 39%)', 'hsl(30, 80%, 55%)', 'hsl(280, 65%, 60%)', 'hsl(340, 75%, 55%)'];

export default function Reports() {
    const [salesData, setSalesData] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [days, setDays] = useState('7');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [salesRes, topRes, catRes] = await Promise.all([
                reportsAPI.getSalesByDate(parseInt(days)),
                reportsAPI.getTopProducts(10),
                reportsAPI.getSalesByCategory()
            ]);
            setSalesData(salesRes.data);
            setTopProducts(topRes.data);
            setCategoryData(catRes.data);
        } catch (error) {
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const totalRevenue = salesData.reduce((sum, d) => sum + d.revenue, 0);
    const totalTransactions = salesData.reduce((sum, d) => sum + d.transactions, 0);
    const avgTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

    if (loading) {
        return (
            <Layout title="Reports">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Reports">
            <div className="space-y-6" data-testid="reports-page">
                {/* Period Selector */}
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">Analytics Overview</h2>
                    <Select value={days} onValueChange={setDays}>
                        <SelectTrigger className="w-[180px]" data-testid="period-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">Last 7 days</SelectItem>
                            <SelectItem value="14">Last 14 days</SelectItem>
                            <SelectItem value="30">Last 30 days</SelectItem>
                            <SelectItem value="90">Last 90 days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card data-testid="total-revenue-card">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-emerald-500/10 rounded-lg">
                                    <DollarSign className="h-6 w-6 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                                    <p className="text-2xl font-bold font-mono">${totalRevenue.toFixed(2)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card data-testid="total-transactions-card">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-500/10 rounded-lg">
                                    <BarChart3 className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Transactions</p>
                                    <p className="text-2xl font-bold font-mono">{totalTransactions}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card data-testid="avg-transaction-card">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-violet-500/10 rounded-lg">
                                    <TrendingUp className="h-6 w-6 text-violet-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Avg Transaction</p>
                                    <p className="text-2xl font-bold font-mono">${avgTransaction.toFixed(2)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Tabs defaultValue="overview" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
                        <TabsTrigger value="products" data-testid="products-tab">Products</TabsTrigger>
                        <TabsTrigger value="categories" data-testid="categories-tab">Categories</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Revenue Chart */}
                            <Card data-testid="revenue-chart">
                                <CardHeader>
                                    <CardTitle>Revenue Trend</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={salesData}>
                                                <defs>
                                                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                                                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                />
                                                <YAxis tickFormatter={(v) => `$${v}`} />
                                                <Tooltip 
                                                    formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']}
                                                    labelFormatter={(v) => new Date(v).toLocaleDateString()}
                                                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                                />
                                                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--chart-1))" fillOpacity={1} fill="url(#colorRev)" strokeWidth={2} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Transactions Chart */}
                            <Card data-testid="transactions-chart">
                                <CardHeader>
                                    <CardTitle>Daily Transactions</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={salesData}>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                />
                                                <YAxis />
                                                <Tooltip 
                                                    formatter={(v) => [v, 'Transactions']}
                                                    labelFormatter={(v) => new Date(v).toLocaleDateString()}
                                                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                                />
                                                <Bar dataKey="transactions" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    <TabsContent value="products">
                        <Card data-testid="top-products-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Package className="h-5 w-5" />
                                    Top Selling Products
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[400px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={topProducts} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                            <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                                            <YAxis dataKey="product_name" type="category" width={150} tick={{ fontSize: 12 }} />
                                            <Tooltip 
                                                formatter={(v, name) => [name === 'revenue' ? `$${v.toFixed(2)}` : v, name === 'revenue' ? 'Revenue' : 'Qty Sold']}
                                                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                            />
                                            <Legend />
                                            <Bar dataKey="revenue" fill="hsl(var(--chart-1))" name="Revenue" radius={[0, 4, 4, 0]} />
                                            <Bar dataKey="quantity_sold" fill="hsl(var(--chart-2))" name="Qty Sold" radius={[0, 4, 4, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="categories">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card data-testid="category-pie-chart">
                                <CardHeader>
                                    <CardTitle>Revenue by Category</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={categoryData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ category, percent }) => `${category}: ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    dataKey="revenue"
                                                >
                                                    {categoryData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip 
                                                    formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']}
                                                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card data-testid="category-bar-chart">
                                <CardHeader>
                                    <CardTitle>Sales by Category</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={categoryData}>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                                <XAxis dataKey="category" />
                                                <YAxis />
                                                <Tooltip 
                                                    formatter={(v, name) => [name === 'revenue' ? `$${v.toFixed(2)}` : v, name === 'revenue' ? 'Revenue' : 'Quantity']}
                                                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                                />
                                                <Legend />
                                                <Bar dataKey="revenue" fill="hsl(var(--chart-1))" name="Revenue" radius={[4, 4, 0, 0]} />
                                                <Bar dataKey="quantity" fill="hsl(var(--chart-3))" name="Quantity" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
